import { Injectable } from '@angular/core';
import { BehaviorSubject, filter, take } from 'rxjs';
import { UserSettingsStreamService } from '../user/user-settings-stream.service';
import { SurveyJob } from './survey-job';

/**
 * Service to manage streams for survey job visibility and selection state
 *
 * This service maintains separate state for survey jobs displayed on the map,
 * independent of tasks and layers menu state. It tracks which jobs are visible
 * and selected, enabling job-based feature filtering on the map.
 */
@Injectable({
    providedIn: 'root'
})
export class SurveyJobStreamsService {
    private localJobVisibilityState = {
        hideAllJobs: false,
        visibleJobIds: [] as string[],
        hiddenJobIds: [] as string[]
    };
    /**
     * Stream of survey jobs that require their features to be displayed on the map
     */
    deferVisibleJobsStream$ = new BehaviorSubject<SurveyJob[]>([]);
    deferHideJobsStream$ = new BehaviorSubject<SurveyJob[]>([]);
    deferVisibleAllJobsStream$ = new BehaviorSubject<boolean>(true);

    constructor(private userSettingsStream: UserSettingsStreamService) {
        this.userSettingsStream.currentMapWorkspaceSettings$
            .pipe(
                filter(settings => settings !== null),
                take(1)
            )
            .subscribe(settings => {
                this.setLocalJobVisibilityState({
                    hideAllJobs: settings.hideAllJobs ?? false,
                    visibleJobIds: settings.visibleJobIds ?? [],
                    hiddenJobIds: settings.hiddenJobIds ?? []
                });
            });
    }

    public deferVisibleAllJobsStream(visible: boolean): void {
        this.setLocalJobVisibilityState({
            hideAllJobs: !visible,
            visibleJobIds: [],
            hiddenJobIds: []
        });
        this.deferVisibleAllJobsStream$.next(visible);
    }

    public setLocalJobVisibilityState(state: {
        hideAllJobs: boolean;
        visibleJobIds: string[];
        hiddenJobIds: string[];
    }): void {
        this.localJobVisibilityState = state;
    }

    public getLocalJobVisibilityState(): { hideAllJobs: boolean; visibleJobIds: string[]; hiddenJobIds: string[] } {
        return this.localJobVisibilityState;
    }
}
